/**
 * Marketing contacts — the address book campaigns actually send to.
 *
 * This is not the Sales contact list and never mixes with it. A sales contact
 * is somebody a deal is being worked with; the rows here are addresses, often
 * hundreds at a time from a CSV, and putting those through the pipeline made
 * Sales unusable. The campaign wizard reads this list and nothing else.
 */
import { useMemo, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { digitsOf, download } from '../../lib/format.js';
import { Card, CardHead, EmptyState, Pill, Avatar, Dash } from '../../components/Primitives.jsx';
import { TileRow } from '../../components/StatCards.jsx';
import { MKT_CONTACT_STATUSES } from '../../constants/marketing.js';
import { isOptedOut, newestFirst } from '../../domain/marketing.js';
import ImportContacts from '../../components/modals/ImportContacts.jsx';

const STATUS_TONE = {
  Subscribed: 'p-green',
  Unconfirmed: 'p-amber',
  Bounced: 'p-orange',
  Unsubscribed: 'p-red'
};

export default function Contacts() {
  const {
    mkt, setSheet, importMktContacts, removeMarketingRecord,
    addOptOut, removeOptOut, toast
  } = useApp();

  const [term, setTerm] = useState('');
  const [status, setStatus] = useState('');
  const [importing, setImporting] = useState(false);

  /* Newest first, so somebody who has just added or imported one sees it. */
  const list = useMemo(() => newestFirst(mkt.contacts), [mkt.contacts]);

  const shown = useMemo(() => {
    const q = term.trim().toLowerCase();
    return list.filter(c => {
      if (status && c.status !== status) return false;
      if (!q) return true;
      return [c.name, c.email, c.phone, c.company]
        .some(v => String(v || '').toLowerCase().includes(q));
    });
  }, [list, term, status]);

  const emailable = list
    .filter(c => c.email && c.status !== 'Unsubscribed' && c.status !== 'Bounced').length;
  const phoneable = list.filter(c => digitsOf(c.phone) && c.status !== 'Unsubscribed').length;

  const exportCSV = () => {
    const q = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const head = ['Name', 'Email', 'Phone', 'Company', 'Status', 'Source', 'Notes', 'Added'];
    const lines = [head.map(q).join(',')].concat(shown.map(c =>
      [c.name, c.email, c.phone, c.company, c.status, c.source, c.notes, c.created]
        .map(q).join(',')));
    download(
      new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }),
      'marketing-contacts.csv'
    );
    toast(`Exported ${shown.length} contact${shown.length > 1 ? 's' : ''}`);
  };

  /*
   * An unsubscribe has to do both: stop the send, and say so on the row. The
   * two are separate lists — the opt-out list is matched on the address, the
   * status belongs to the contact — so putting somebody back means undoing
   * both, or the row would read Subscribed and still be skipped.
   */
  const unsubscribe = c => {
    addOptOut(String(c.email || digitsOf(c.phone)));
    importMktContacts([{ ...c, status: 'Unsubscribed' }]);
    toast(`${c.name} will not be contacted again`);
  };

  const resubscribe = c => {
    [String(c.email || '').trim().toLowerCase(), digitsOf(c.phone)]
      .filter(Boolean)
      .forEach(key => {
        const i = mkt.optouts.indexOf(key);
        if (i >= 0) removeOptOut(i);
      });
    importMktContacts([{ ...c, status: 'Subscribed' }]);
    toast(`${c.name} can be contacted again`);
  };

  const remove = c => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${c.name} from the marketing list?`)) return;
    removeMarketingRecord('contacts', c.id);
    toast('Contact removed');
  };

  return (
    <>
      <TileRow
        tiles={[
          ['Contacts', list.length, 'users', 'g-pink'],
          ['Emailable', emailable, 'mail', 'g-blue'],
          ['On a number', phoneable, 'chat', 'g-green'],
          ['Subscribed', list.filter(c => c.status === 'Subscribed').length, 'check', 'g-purple'],
          ['Unsubscribed', list.filter(c => c.status === 'Unsubscribed').length, 'alert', 'g-red'],
          ['Bounced', list.filter(c => c.status === 'Bounced').length, 'x', 'g-orange']
        ]}
      />

      <Card>
        <CardHead icon="users" tone="g-pink" title="Marketing contacts" count={list.length}>
          <button
            type="button"
            className={`btn sm${importing ? ' primary' : ''}`}
            onClick={() => setImporting(v => !v)}
          >
            <Icon name="up" />Import
          </button>
          <button type="button" className="btn sm" onClick={exportCSV} disabled={!shown.length}>
            <Icon name="down" />Export
          </button>
          <button
            type="button"
            className="btn sm primary"
            onClick={() => setSheet({ kind: 'mktContact' })}
          >
            <Icon name="plus" />New contact
          </button>
        </CardHead>

        {importing ? <ImportContacts onClose={() => setImporting(false)} /> : null}

        {list.length ? (
          <>
            <div className="crow">
              <input
                className="csearch"
                value={term}
                placeholder="Search by name, email, phone or company…"
                onChange={e => setTerm(e.target.value)}
              />
              <select value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">Any status</option>
                {MKT_CONTACT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span className="chint">{shown.length} of {list.length} shown</span>
            </div>

            {shown.length ? (
              <div className="tscroll">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th><th>Email</th><th>Phone</th><th>Company</th>
                      <th>Status</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map(c => {
                      const out = isOptedOut(mkt, c);
                      return (
                        <tr key={c.id} onClick={() => setSheet({ kind: 'mktContact', id: c.id })}>
                          <td>
                            <div className="cname">
                              <Avatar name={c.name} />
                              <div>
                                <b>{c.name}</b>
                                {c.source ? <small>{c.source}</small> : null}
                              </div>
                            </div>
                          </td>
                          <td className="dim">{c.email || <Dash />}</td>
                          <td className="dim">{c.phone || <Dash />}</td>
                          <td className="dim">{c.company || <Dash />}</td>
                          <td>
                            <Pill tone={STATUS_TONE[c.status] || 'p-grey'}>
                              {c.status || 'Subscribed'}
                            </Pill>
                          </td>
                          <td>
                            <div className="acts">
                              <button
                                type="button"
                                title="Edit"
                                onClick={e => {
                                  e.stopPropagation();
                                  setSheet({ kind: 'mktContact', id: c.id });
                                }}
                              >
                                <Icon name="edit" />
                              </button>
                              {out || c.status === 'Unsubscribed' ? (
                                <button
                                  type="button"
                                  title="Put back on the list"
                                  onClick={e => { e.stopPropagation(); resubscribe(c); }}
                                >
                                  <Icon name="refresh" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  title="Unsubscribe"
                                  onClick={e => { e.stopPropagation(); unsubscribe(c); }}
                                >
                                  <Icon name="bell" />
                                </button>
                              )}
                              <button
                                type="button"
                                className="rm"
                                title="Remove"
                                onClick={e => { e.stopPropagation(); remove(c); }}
                              >
                                <Icon name="trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted">Nothing matches those filters.</p>
            )}
          </>
        ) : (
          <EmptyState icon="users" tone="g-pink" title="No marketing contacts yet">
            These are separate from your sales contacts — a list to send to, not a pipeline to
            work. Add one, or import a CSV with name, email and phone columns.
            <div className="estart">
              <button
                type="button"
                className="btn primary"
                onClick={() => setSheet({ kind: 'mktContact' })}
              >
                Add the first contact
              </button>
              <span>Or import a list — existing addresses are updated rather than duplicated.</span>
            </div>
          </EmptyState>
        )}
      </Card>
    </>
  );
}
